import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { REQUEST_THREAD_ENTITY } from '../../../../constants/entity'
import { FILE_SCOPE_REQUEST } from '../../../../constants/file'
import { useSelection } from '../../../../hooks'
import { isListViewTypeWithPreview } from '../../../../utils/listViewType'
import {
  EMAIL_ITEM_ID_KEY,
  EMAIL_TABLE_ROW_ACTIONS_WIDTH,
  EmailTableRow,
  EmailTableRowActions,
} from '../../../Email'
import { FilterDivider } from '../../../FilterDivider'
import {
  MINIMIZED_MODAL_KEY,
  MINIMIZED_MODAL_SCOPE,
  useMinimizableModalOpener,
} from '../../../Modal'
import { useMultipleViewTypeListPreviewActions } from '../../../MultipleViewTypeList'
import { PageActions } from '../../../Page'
import { PageEmptyState } from '../../../PageEmptyState'
import { Pagination } from '../../../Pagination'
import { SelectionToolbar } from '../../../SelectionToolbar'
import {
  getSelectedSize,
  Table,
  TableConfigModal,
  TableContext,
  TableControlBar,
  TableControlButtons,
  TableMassAction,
  TableViewRegimeControl,
} from '../../../Table'
import { TablePagination } from '../../../TablePagination'
import { useRequestProfileTabPerformingAction } from '../../RequestProfile.hooks'
import {
  REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTIONS_CLOSING_ACTIVE_PREVIEW,
  REQUEST_PROFILE_TAB_EMAILS_MASS_ACTIONS_CLOSING_ACTIVE_PREVIEW,
  REQUEST_PROFILE_TAB_EMAILS_SELECTION_CONFIG,
} from '../RequestProfileTabEmails.constants'
import { useRequestProfileTabEmailsActions } from '../RequestProfileTabEmails.hooks'
import { getIsRequestProfileTabEmailsListEmpty } from '../RequestProfileTabEmails.utils'
import { RequestProfileTabEmailsThreadModal } from '../RequestProfileTabEmailsThreadModal'

import {
  getRequestProfileTabEmailsEmptyStateKey,
  getRequestProfileTabEmailsItemActions,
  getRequestProfileTabEmailsMassActions,
} from './RequestProfileTabEmailsTable.utils'

import * as styles from './RequestProfileTabEmailsTable.module.css'

import EmailIcon from '../../../../icons/email.svg'

function RequestProfileTabEmailsTable({
  activeItem,
  columns,
  filterValues,
  handleReload,
  handleSetColumns,
  handleSetPagination,
  handleSetSize,
  handleToggleDense,
  handleUpdateConfig,
  hasError,
  isDense,
  isLoading,
  isRefetching,
  isSearchResult,
  items,
  listViewType,
  ordering,
  pagination,
  permissions,
  profileLoadersRef,
  requestId,
  requiredColumns,
  scrollContainerRef,
  setActiveItem,
  shouldCloseActivePreviewRef,
  subMenu,
  onChangeFocusedRow,
  onChangeViewType,
  onClickRowPreview,
  onCloseModal,
  onCloseThreadModal,
  onMouseDownRowPreview,
  onSetModal,
  onSuccessRead,
}) {
  const { t } = useTranslation()

  const containerRef = useRef(null)

  const [isConfigOpen, setConfigOpen] = useState(false)

  const {
    checkIsItemSelected,
    exclude,
    handleCleanSelection,
    handleExclude,
    handleSelect,
    handleSelectAll,
    handleSelectAllPages,
    handleUnselectAllPages,
    isAllSelected,
    isRemoveSelection,
    selectAllPages,
    selected,
  } = useSelection({
    config: REQUEST_PROFILE_TAB_EMAILS_SELECTION_CONFIG,
    data: items,
    pagination,
  })

  async function handleReloadAll() {
    handleCleanSelection()

    return Promise.all([handleReloadActivePreview(), handleReload()])
  }

  const { isPerformingAction, setImportant, setItemAction, setMassAction } =
    useRequestProfileTabEmailsActions({
      exclude,
      filterValues,
      requestId,
      selectAllPages,
      selected,
      onCleanSelection: handleCleanSelection,
      onCloseModal,
      onReload: handleReloadAll,
      onSetModal,
    })

  const {
    handleReloadActivePreview,
    handleSetItemAction,
    handleSetMassAction,
  } = useMultipleViewTypeListPreviewActions({
    activeItem,
    excludeIds: exclude,
    isPerformingMassAction: isPerformingAction,
    itemActionsClosingActivePreview:
      REQUEST_PROFILE_TAB_EMAILS_ITEM_ACTIONS_CLOSING_ACTIVE_PREVIEW,
    itemIdKey: EMAIL_ITEM_ID_KEY,
    listViewType,
    massActionsClosingActivePreview:
      REQUEST_PROFILE_TAB_EMAILS_MASS_ACTIONS_CLOSING_ACTIVE_PREVIEW,
    minimizedModalScope: MINIMIZED_MODAL_SCOPE.EMAIL_PROFILE,
    profileLoadersRef,
    selectAllPages,
    selectedIds: selected,
    setActiveItem,
    setItemAction,
    setMassAction,
    shouldCloseActivePreviewRef,
  })

  useEffect(() => {
    handleCleanSelection()
  }, [filterValues])

  useRequestProfileTabPerformingAction(isRefetching || isPerformingAction)

  const massActions = getRequestProfileTabEmailsMassActions({
    permissions,
    subMenu,
    t,
  })

  function getItemActions(item) {
    return getRequestProfileTabEmailsItemActions({ item, subMenu })
  }

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  function openThreadModal(mailUuid) {
    onSetModal(
      <RequestProfileTabEmailsThreadModal
        mailUuid={mailUuid}
        navigationContext={{ filterValues, ordering, requestId }}
        requestId={requestId}
        onClose={onCloseThreadModal}
        onReload={handleReloadAll}
        onSuccessRead={onSuccessRead}
      />
    )
  }

  const { openModal: handleOpenThreadModal } = useMinimizableModalOpener({
    minimizeKey: MINIMIZED_MODAL_KEY.REQUEST_PROFILE_EMAIL_THREAD,
    onOpen: openThreadModal,
  })

  const isPreviewListViewType = isListViewTypeWithPreview(listViewType)

  const isEmpty = getIsRequestProfileTabEmailsListEmpty({ isLoading, items })

  return hasError || isEmpty ? (
    <div className={styles.RequestProfileTabEmailsTable}>
      <PageEmptyState
        EntityIcon={EmailIcon}
        className={styles.emptyPage}
        text={t(
          getRequestProfileTabEmailsEmptyStateKey({ hasError, isSearchResult })
        )}
      />
    </div>
  ) : (
    <div className={styles.RequestProfileTabEmailsTable} ref={containerRef}>
      <TableControlBar>
        <TableMassAction
          responsive
          actions={massActions}
          isAllSelected={isAllSelected}
          isLoading={isLoading}
          isRemoveSelection={isRemoveSelection}
          selectedSize={getSelectedSize({
            exclude,
            pagination,
            selectAllPages,
            selected,
          })}
          setMassAction={handleSetMassAction}
          onSelectAll={handleSelectAll}
        />
        <PageActions isLoading={isLoading}>
          <TableControlButtons
            ChangeViewTypeButton={
              onChangeViewType ? TableViewRegimeControl : null
            }
            ResetSortingButton={null}
            changeViewTypeButtonProps={{
              listViewType,
              onChange: onChangeViewType,
            }}
            configButtonProps={{ onClick: handleConfigOpen }}
            densityButtonProps={{
              isDense,
              onClick: handleToggleDense,
            }}
            refreshButtonProps={{
              isLoading: isRefetching,
              onClick: handleReload,
            }}
          />
          <FilterDivider />
          <Pagination
            responsive
            value={pagination}
            onChange={handleSetPagination}
            onChangeSize={handleSetSize}
          />
        </PageActions>
      </TableControlBar>
      <Table
        TableRow={EmailTableRow}
        actionsColumnWidth={EMAIL_TABLE_ROW_ACTIONS_WIDTH}
        arrowNavigationConfig={{
          disabled: !isPreviewListViewType,
          focusedRow: activeItem,
          itemIdKey: EMAIL_ITEM_ID_KEY,
        }}
        checkIsItemSelected={checkIsItemSelected}
        columns={columns}
        containerRef={scrollContainerRef ?? containerRef}
        context={TableContext.Emails}
        headerRowProps={{
          onApply: handleUpdateConfig,
          onSetColumns: handleSetColumns,
        }}
        isLoading={isLoading}
        items={items?.items}
        rowProps={{
          Actions: EmailTableRowActions,
          activeItem,
          attachedFilesProps: {
            scope: FILE_SCOPE_REQUEST,
            subject: requestId,
          },
          getActions: getItemActions,
          hideCheckbox: massActions.length === 0,
          isShort: isDense,
          singleEmailAccount: false,
          onCallAction: handleSetItemAction,
          onClick: isPreviewListViewType
            ? onClickRowPreview
            : handleOpenThreadModal,
          onMouseDown: isPreviewListViewType ? onMouseDownRowPreview : null,
          onSelect: selectAllPages ? handleExclude : handleSelect,
          onSetImportant: setImportant,
        }}
        onChangeFocusedRow={onChangeFocusedRow}
      />
      <TablePagination
        responsive
        isSkeleton={isLoading}
        value={pagination}
        onChange={handleSetPagination}
      />
      {selected.length > 0 || selectAllPages ? (
        <SelectionToolbar
          allSelected={selectAllPages || selected.length === pagination.count}
          count={pagination.count}
          excludeCount={exclude.length}
          selected={selected.length}
          onClose={handleUnselectAllPages}
          onSelectAll={handleSelectAllPages}
        />
      ) : null}
      {isConfigOpen ? (
        <TableConfigModal
          columns={columns}
          entity={REQUEST_THREAD_ENTITY}
          requiredColumns={requiredColumns}
          setColumns={handleSetColumns}
          setConfigOpen={setConfigOpen}
          onApply={handleUpdateConfig}
        />
      ) : null}
    </div>
  )
}

export default RequestProfileTabEmailsTable
